import { useTab } from '@mui/base/useTab';
import cn from 'classnames';
import { forwardRef } from 'react';

import type { ForwardedRef, ReactElement } from 'react';

import type { TabProps } from './tab.types';

import './tab.scss';

/**
 * The tab element itself.
 * Clicking on a tab displays its corresponding panel.
 *
 * ### Usages
 *
 * ```tsx
 * import { Tabs, TabList, Tab, TabPanel } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return (
 *     <Tabs>
 *       <TabList>
 *         <Tab value="tab-1">Tab 1</Tab>
 *         <Tab value="tab-2" disabled>Tab 2</Tab>
 *         <Tab value="tab-3" startIcon={<SomeIcon />}>Tab 3</Tab>
 *       </TabList>
 *       <TabPanel value="tab-1">Tab 1 content</TabPanel>
 *       <TabPanel value="tab-2">Tab 2 content</TabPanel>
 *       <TabPanel value="tab-3">Tab 3 content</TabPanel>
 *     </Tabs>
 *   );
 * }
 * ```
 */
const Tab = forwardRef((props: TabProps, forwardedRef: ForwardedRef<Element> | null): ReactElement => {
  const {
    children,
    className,
    disabled = false,
    onClick,
    startIcon,
    value,
    ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
  } = props;

  const handleClick = (event: React.MouseEvent<Element, MouseEvent>): void => {
    onClick?.(event, value);
  };

  const { focusVisible, getRootProps, selected } = useTab({
    ...props,
    disabled,
    rootRef: forwardedRef,
    value,
  });

  const { type: _unusedType, ...rootProps } = getRootProps({
    onClick: handleClick,
    ...rest,
  });

  if (disabled) {
    rootProps.tabIndex = -1;
  }

  const classes = cn(
    'rbui-tab',
    {
      'rbui-tab--disabled': disabled,
      'rbui-tab--focus-visible': focusVisible,
      'rbui-tab--has-icon': startIcon,
      'rbui-tab--selected': selected,
    },
    className,
  );

  return (
    <div {...rootProps} className={classes}>
      {startIcon && (
        <span className="rbui-tab__start-icon" role="img">
          {startIcon}
        </span>
      )}
      {children}
    </div>
  );
});

Tab.displayName = 'Tab';

export default Tab;
